import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOptionalActionEditorUnsaved } from '../context/ActionEditorUnsavedContext';
import {
  hasActionDraftChanged,
  normalizeActionDraft,
} from '../utils/actionEditorDraftUtils';

/**
 * Track dirty state for an open action editor and guard close/cancel actions.
 */
export function useActionEditorDraft({
  editorId,
  isOpen,
  step,
  draft,
  mapStepToDraft,
}) {
  const unsavedContext = useOptionalActionEditorUnsaved();
  const [baseline, setBaseline] = useState(null);
  const mapStepToDraftRef = useRef(mapStepToDraft);
  mapStepToDraftRef.current = mapStepToDraft;

  useEffect(() => {
    if (!isOpen || !step) return;
    const mapper = mapStepToDraftRef.current;
    const nextBaseline = mapper
      ? mapper(step)
      : (step?.config || {});
    setBaseline(normalizeActionDraft(nextBaseline));
  }, [isOpen, step?.id]);

  const normalizedDraft = useMemo(
    () => normalizeActionDraft(draft),
    [draft],
  );

  const isDirty = isOpen && hasActionDraftChanged(baseline, normalizedDraft);

  useEffect(() => {
    if (!unsavedContext || !editorId) return undefined;
    if (isOpen) unsavedContext.setEditorDirty(editorId, isDirty);
    return () => unsavedContext.setEditorDirty(editorId, false);
  }, [unsavedContext, editorId, isOpen, isDirty]);

  const commitDraft = useCallback(() => {
    setBaseline(normalizedDraft);
    if (unsavedContext && editorId) {
      unsavedContext.setEditorDirty(editorId, false);
    }
  }, [normalizedDraft, unsavedContext, editorId]);

  const guardedClose = useCallback((onClose) => {
    if (!unsavedContext || !isDirty) {
      onClose?.();
      return;
    }
    unsavedContext.guardAction(() => onClose?.());
  }, [unsavedContext, isDirty]);

  return {
    isDirty,
    guardedClose,
    commitDraft,
  };
}
